import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import service from "../../../api/services";
import Wrapper from "../../../components/common/Wrapper/Wrapper";
import organisms from "../../../components/organisms";
import {
  DefaultPermissionType,
  FormatDefaultPermissionType,
  ISelectedRole,
  PermissionPayloadType,
  StorePermissionApiType,
  SubAdminDataProps,
  SubAdminPermissionType,
  SubAdminUserEditDataProps,
  TRoleDataProps,
  TStoreList,
} from "./interface";
import { useRole } from "./hook/useRole";

const { StaffAdd } = organisms;

const Index = () => {
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const {
    getSubAdminRoleOption,
    handleSelectedRole,
    modifiedSubAdminApiData,
    handleStorePermissionToggle,
  } = useRole();
  const [storeList, setStoreList] = useState<TStoreList[] | []>([]);
  const [defaultPermission, setDefaultPermission] =
    useState<DefaultPermissionType>({});
  const [permissionPayload, setPermissionPayload] =
    useState<PermissionPayloadType>({});
  const [subAdminPermission, setSubAdminPermission] =
    useState<SubAdminPermissionType>({});

  const [rolesList, setRolesList] = useState<ISelectedRole[]>([]);
  const [rolesData, setRolesData] = useState<TRoleDataProps[]>();

  let params = useParams();
  let staffEditID = params.id || "";
  const [buttonClick, setButtonClick] = useState<boolean>(false);

  const [subAdmin, setSubAdmin] = useState<SubAdminDataProps>({
    login_id: "",
    username: "",
    mobile: "",
    email: "",
    password: "",
    role_id: "",
    store_id: [],
  });

  // filter the permission for sending in the api
  const getFilteredPermissionPayload = (): {
    store_id: string;
    permissions: string[];
  }[] => {
    const filtered: PermissionPayloadType = {};

    for (const id in permissionPayload) {
      if (subAdminPermission[id]?.isChecked) {
        filtered[id] = permissionPayload[id];
      }
    }

    return Object.entries(filtered).map(([store_id, permissions]) => ({
      store_id,
      permissions,
    }));
  };
  // end

  // INFO :- function to formate the data to set default permission in case of edit subAdmin
  const formatDefaultPermission = (
    data: StorePermissionApiType[]
  ): FormatDefaultPermissionType => {
    const result: FormatDefaultPermissionType = {};

    data?.forEach(({ store_id, permissions }) => {
      result[store_id] = {
        isChecked: true,
        permission: permissions,
      };
    });

    return result;
  };
  // end

  // function to show the error message
  const showErrorMessage = (): boolean => {
    // Retrieve the filtered permission payload (selected stores)
    const stores = getFilteredPermissionPayload();
    let errorMessage = "";

    // Case 1: No stores selected
    if (!stores?.length) {
      errorMessage = "Please select at least one store.";
    } else {
      // Case 2: All selected stores have no permissions assigned
      const storesWithoutPermissions = stores.filter(
        (store) => !store.permissions?.length
      );
      if (storesWithoutPermissions.length === stores.length) {
        errorMessage =
          "Please select at least one permission for one of the selected stores.";
      }
    }

    // If an error message was set, show the toast and return false
    if (errorMessage) {
      addToast(errorMessage, {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
      return false;
    }

    // All validations passed
    return true;
  };
  // end

  // INFO:- function to add the sub-admin
  const addSubAdmin = async () => {
    let payload = {
      mobile: subAdmin?.mobile,
      email: subAdmin?.email,
      password: subAdmin?.password,
      username: subAdmin?.username,
      user_id: subAdmin?.login_id,
      role_id: (subAdmin?.role_id as ISelectedRole)?.key,
      // store_ids: storeID,
      stores: getFilteredPermissionPayload(),
      // role: staff?.value,
    };

    if (showErrorMessage()) {
      try {
        let response: any;
        if (staffEditID) {
          response = await service.staff.edit(staffEditID, payload);
          if (response.status === 200) {
            addToast(response?.data?.message, {
              appearance: "success",
              autoDismiss: true,
              autoDismissTimeout: 3000,
            });
            navigate(-1);
          }
        } else {
          response = await service.staff.add(payload);
          if (response.status === 201) {
            addToast(response?.data?.message, {
              appearance: "success",
              autoDismiss: true,
              autoDismissTimeout: 3000,
            });
            navigate(-1);
          }
        }
      } catch (error: any) {
        console.error(error);
        addToast(error?.response?.data?.message, {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
      }
    }
    setButtonClick(false);
  };
  // end

  // INFO:- function to get the sub-admin data at time of update the sub-admin
  const getDataById = async () => {
    try {
      // Critical: Exit the function early if rolesData is not available
      if (!rolesData || !(storeList?.length > 0)) return;

      let response = await service.staff.getDetailById(staffEditID);
      const sunAdminArray = response.data?.data?.user?.store_staffs || [];
      handleEditData(response?.data?.data.user);
      let modifiedData = modifiedSubAdminApiData({
        sunAdminArray,
        storeList,
        rolesData,
        role_id: response?.data?.data?.user?.role?.id,
        getStructuredPermissionStoreWise,
      });

      setDefaultPermission(formatDefaultPermission(modifiedData));
    } catch (error: any) {
      console.error(error);
      addToast(error.response.data.message, {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
    }
  };
  // end

  // INFO:- function to set the sub-admin data to variables
  const handleEditData = (response: SubAdminUserEditDataProps) => {
    let storeID: any = [];
    response &&
      response?.store_staffs?.map((value) => {
        storeID.push({
          key: value.store.id,
          value: value.store.id,
          label: value.store.store_location,
        });
      });
    let roleById = {
      key: response?.role?.id,
      value: response?.role?.role_name,
      label: response?.role?.role_name,
    };
    let tempEdit: SubAdminDataProps = {
      mobile: response?.mobile,
      email: response?.email,
      username: response?.username,
      login_id: response?.login_id,
      store_id: storeID,
      password: "",
      role_id: response?.role ? roleById : "",
    };
    setSubAdmin(tempEdit);
  };
  // end

  //INFO:- function to handle password change
  const handleChangePassword = async (password: any) => {
    let payload = {
      password: password,
    };
    try {
      let response = await service.staff.changePassword(staffEditID, payload);
      if (response.status === 200) {
        addToast("Password Change successfully", {
          appearance: "success",
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
      }
    } catch (error: any) {
      console.error(error);
      addToast(error.response.data.message, {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
    }
  };
  // end

  // INFO:- function to get the store list
  const getStoreList = async () => {
    try {
      const storeListApiResponse = await service.store.storeList();
      if (storeListApiResponse.status === 200) {
        const storeData = storeListApiResponse?.data?.data;

        // Build dropdown with showStore flag
        const storeDropDown: TStoreList[] = await Promise.all(
          storeData.map(
            async (item: {
              id: string;
              store_name: string;
              store_location: string;
            }) => {
              let showStore = true;
              if (!staffEditID) {
                const result = await getStructuredPermissionStoreAPIWise(
                  item.id,
                  false
                );
                showStore = result ?? true;
              } else {
                const result = await getStructuredPermissionStoreAPIWise(
                  item.id,
                  true
                );
                showStore = result ?? true;
              }
              return {
                label: item?.store_location || item.store_name,
                value: item.id,
                showStore,
              };
            }
          )
        );

        setStoreList(storeDropDown);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // end

  // INFO:- function to get the structure permission of store
  const getStructuredPermissionStoreWise = async (
    id: string,
    isChecked: boolean
  ) => {
    if (subAdminPermission && subAdminPermission.hasOwnProperty(id)) {
      // Toggle isChecked if permission already exists
      setSubAdminPermission((prevState) => ({
        ...prevState,
        [id]: {
          ...prevState[id],
          isChecked: isChecked,
        },
      }));
    } else {
      // Fetch and set permission if it doesn't exist
      getStructuredPermissionStoreAPIWise(id, false);
    }
  };
  // end

  // INFO:- function to get the structure permission of store wise api
  const getStructuredPermissionStoreAPIWise = async (
    id: string,
    isChecked: boolean
  ): Promise<boolean | undefined> => {
    try {
      const response = await service.staff?.subAdminPermission(id,{for_staff_portal:true});
      setSubAdminPermission((prevState) => ({
        ...prevState,
        [id]: {
          isChecked: isChecked,
          permission: response.data.data,
        },
      }));
      if (!staffEditID) {
        setDefaultPermission((prevState) => ({
          ...prevState,
          [id]: {
            permission: response.data.data,
            isChecked: true,
          },
        }));
      }
      // setDefaultStorePermission((prevState) => ({
      //     ...prevState,
      //     [id]: {
      //         permission: response.data.data,
      //         isChecked: true,
      //     },
      // }));
      //for check the subAdmin permission is given to store or not
      return response.data.data;
      //end
    } catch (error: any) {
      console.error(error);
      addToast(error.response.data.message, {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
    }
  };
  // end

  // INFO:- function to prepare the permission code to api
  const _handleCheckboxChange = (codeArr: string[], id: string) => {
    setPermissionPayload((prevState) => ({
      ...prevState,
      [id]: codeArr,
    }));
  };
  // end

  // INFO:- useEffect to handle the create and update api
  useEffect(() => {
    if (buttonClick) {
      addSubAdmin();
    }
  }, [buttonClick]);
  // end

  // Fetch initial data
  useEffect(() => {
    getStoreList();
    getSubAdminRoleOption({ setRolesList, setRolesData });
  }, []);
  // end

  useEffect(() => {
    if (staffEditID && storeList && rolesData) {
      getDataById();
    }
  }, [rolesData, storeList]);

  return (
    <Wrapper>
      <StaffAdd
        staff={subAdmin}
        setStaff={setSubAdmin}
        setButtonClick={setButtonClick}
        handleChangePassword={(password: any) => handleChangePassword(password)}
        storeList={storeList}
        defaultPermission={defaultPermission}
        getStructuredPermissionStoreWise={(id: string, isChecked: boolean) =>
          getStructuredPermissionStoreWise(id, isChecked)
        }
        subAdminPermission={subAdminPermission}
        _handleCheckboxChange={_handleCheckboxChange}
        rolesList={rolesList}
        handleStorePermissionToggle={(id: string, isChecked: boolean) =>
          handleStorePermissionToggle({
            id,
            isChecked,
            getStructuredPermissionStoreWise,
            rolesData,
            role_id: (subAdmin?.role_id as ISelectedRole)?.key,
            setDefaultPermission,
            subAdminPermission,
            defaultPermission,
          })
        }
        handleSelectedRole={(role: ISelectedRole) =>
          handleSelectedRole({
            role,
            rolesData,
            defaultPermission: subAdminPermission,
            setDefaultPermission,
            subAdmin,
            setSubAdmin,
          })
        }
      />
    </Wrapper>
  );
};
export default Index;
